import 'vite/modulepreload-polyfill';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { createApp, onMounted, onUnmounted, ref, watch } from 'vue';

import * as AnalyticsData from '~/analytics/analytics-data';
import { useCart } from '~/composables/use-cart';
import { useCartDrawerVisibility } from '~/composables/use-cart-drawer-visibility';
import { useFocusManagement } from '~/composables/use-focus-management';
import { useSizeChart } from '~/composables/use-size-chart';
import { useViewportSize } from '~/composables/use-viewport-size';
import { ANIMATION_CONFIGS, ANIMATION_DURATIONS, ANIMATION_EASES } from '~/constants/animations';
import { MARKETING_USER_PREFERENCES, TRACK_CART_SOURCE } from '~/constants/rudderstack';
import * as DataDogLogs from '~/lib/datadog/datadog';
import * as Iterable from '~/lib/iterable/iterable';
import * as RudderstackClient from '~/lib/rudderstack/rudderstack-client';
import * as RudderstackUtils from '~/lib/rudderstack/rudderstack-utils';
import * as CustomerUtils from '~/utils/customer';
import { buildGiftCardLineItemProperties } from '~/utils/gift-card-util';
import getItemCartProperties from '~/utils/item-cart-properties';
import { updateHistory } from '~/utils/url';
import { dateValidator, isEmail, messageValidator, nameValidator } from '~/utils/validator';

import { BaseSection } from './base-section';

gsap.registerPlugin(ScrollTrigger);

const DOCKING_DIRECTIONS = {
  DOWN: 'down',
  UP: 'up',
};

/**
 * PDPATCBarSection
 */
class ProductATCBarSection extends BaseSection {
  constructor() {
    super('[data-section-type="product-atc-bar"]');
  }

  init(instanceData) {
    const container = instanceData.container;

    createApp({
      name: 'ProductATCBarSection',
      setup() {
        const { addSingleVariant } = useCart();
        const scrollTriggerInstance = ref(null);
        const currentVariants = ref([]);
        const activeVariant = ref(null);
        const isSizeSelected = ref(false);
        const isUnavailableSelected = ref(false);
        const isSizePopoverOpen = ref(false);
        const isNotifyModalOpen = ref(false);
        const isNotifyModalSuccess = ref(false);
        const isNotifyModalError = ref(false);
        const isSendAsGiftModalOpen = ref(false);
        const sendAsGiftModalToEmail = ref(null);
        const sendAsGiftModalToName = ref(null);
        const sendAsGiftModalSendOn = ref(null);
        const sendAsGiftModalGreeting = ref(null);
        const isOneSizeProduct = ref(false);
        const notifyEmail = ref('');
        const { isMobile } = useViewportSize();
        const sizePopoverTimeline = ref(null);
        const cartVisibility = useCartDrawerVisibility();

        const { openSizeChart } = useSizeChart();

        const sizePopoverContainerRef = ref(null);
        const sizePopoverTriggerRef = ref(null);
        const floatingContainerMobileRef = ref(null);
        const floatingContainerDesktopRef = ref(null);
        const sizePopoverBackgroundRef = ref(null);
        const sizePopoverContentRef = ref(null);
        const productTags = ref([]);

        const initializeElementRefs = () => {
          sizePopoverContainerRef.value = container.querySelector('[data-size-popover-container]');
          sizePopoverTriggerRef.value = container.querySelector('[data-size-popover-trigger]');
          floatingContainerMobileRef.value = container.querySelector(
            '[data-floating-container-mobile]',
          );
          floatingContainerDesktopRef.value = container.querySelector(
            '[data-floating-container-desktop]',
          );

          if (sizePopoverContainerRef.value) {
            sizePopoverBackgroundRef.value =
              sizePopoverContainerRef.value.querySelector('[data-background]');
            sizePopoverContentRef.value = sizePopoverContainerRef.value.querySelector(
              '[data-size-popover-content]',
            );
          }
        };

        const handleDocking = (direction, absolute = false) => {
          const floatingContainer = isMobile.value
            ? floatingContainerMobileRef.value
            : floatingContainerDesktopRef.value;

          if (!floatingContainer) return;

          const isAbsoluted = floatingContainer.style.position === 'absolute';
          let tweenConfig = {
            duration: ANIMATION_DURATIONS.ELEMENT.HOVER.DEFAULT * 0.25,
          };

          if (direction === DOCKING_DIRECTIONS.UP && !absolute) {
            tweenConfig = {
              ...ANIMATION_CONFIGS.FADE_OUT_CTA,
              ...tweenConfig,
              pointerEvents: 'none',
            };
          } else {
            tweenConfig = {
              ...ANIMATION_CONFIGS.FADE_IN_CTA,
              ...tweenConfig,
              pointerEvents: 'auto',
            };

            if (isAbsoluted) {
              gsap.set(floatingContainer, {
                position: 'fixed',
              });
            }
          }

          gsap.to(floatingContainer, tweenConfig);

          if (absolute) {
            gsap.set(floatingContainer, {
              position: 'absolute',
            });
          }
        };

        const fadeIn = () => {
          gsap.to(container, ANIMATION_CONFIGS.FADE_IN, 0);
        };

        const setupScrollTrigger = () => {
          scrollTriggerInstance.value = ScrollTrigger.create({
            end: 'bottom bottom',
            endTrigger: document.querySelector('main'),
            once: false,
            onEnter: () => {
              handleDocking(DOCKING_DIRECTIONS.DOWN);
            },
            onEnterBack: () => {
              handleDocking(DOCKING_DIRECTIONS.DOWN);
            },
            onLeave: () => {
              handleDocking(DOCKING_DIRECTIONS.UP, true);
            },
            onLeaveBack: () => {
              handleDocking(DOCKING_DIRECTIONS.UP);
            },
            start: 'bottom top',
            trigger: container,
          });
        };

        const setupVariants = () => {
          isOneSizeProduct.value = container.dataset.productOneSize === 'true';

          const variants = container.querySelectorAll('[data-variant-id]');

          variants.forEach((variant) => {
            currentVariants.value.push({
              available: variant.dataset.variantAvailable === 'true',
              colorName: container.dataset.productColor,
              compareAtPrice: Number(variant.dataset.variantCompareAtPrice || 0),
              id: Number(variant.dataset.variantId),
              price: Number(variant.dataset.variantPrice || 0),
              productTitle: container.dataset.productName,
              sku: variant.dataset.variantSku,
              title: variant.dataset.variantTitle,
              titleHandleized: variant.dataset.variantTitleHandleized,
            });
          });

          if (isOneSizeProduct.value) {
            selectVariant(currentVariants.value[0].id);
          }
        };

        const closeSizePopover = () => {
          isSizePopoverOpen.value = false;
        };

        const openSizePopover = () => {
          isSizePopoverOpen.value = true;
        };

        const openNotifyModal = () => {
          isNotifyModalOpen.value = true;
        };

        const closeNotifyModal = () => {
          isNotifyModalOpen.value = false;
          isNotifyModalSuccess.value = false;
        };

        const handleNotifySubmit = () => {
          if (!notifyEmail.value || !activeVariant.value || !isEmail(notifyEmail.value)) return;

          const { isMobile } = useViewportSize();
          const origination = MARKETING_USER_PREFERENCES.PLACEMENT.PRODUCT_UPDATES_FORM;
          const email = notifyEmail.value;

          try {
            const iterablePayload = Iterable.buildPayload({
              email,
              isMobile: isMobile.value,
              pdpSignupFlow: Iterable.API_KEYWORDS.PDP_SIGNUP_FLOW_SOLD_OUT,
              placementSource: origination,
              productData: Iterable.buildProductDataObject({
                productColor: container.dataset.productColor,
                productEdition: container.dataset.productEdition,
                productGender: container.dataset.productGender,
                productMaster: container.dataset.productMaster,
                productMaterial: container.dataset.productMaterial,
                productName: container.dataset.productName,
                productSilhouette: container.dataset.productSilhouette,
                sku: activeVariant.value?.sku,
              }),
            });

            // Update user state
            CustomerUtils.identifyActionEmail(email, CustomerUtils.getIdentifiedUserState());

            // Identify user in Rudderstack
            RudderstackClient.identifyUser({
              additionalTraits: RudderstackUtils.getUserMarketingEmailTraits({
                subscriptionSource: origination,
                subscriptionStatus: true,
              }),
              email,
            });

            // Update user marketing preferences
            RudderstackClient.userMarketingPreference({
              cmsProductId: container.dataset.productId || null,
              messageMedium: MARKETING_USER_PREFERENCES.MEDIUM.EMAIL,
              messageType: MARKETING_USER_PREFERENCES.TYPE.ALL,
              optInStatus: true,
              placement: origination,
              sku: activeVariant.value?.sku || null,
            });

            // Push data to Iterable
            Iterable.pushSignUp(iterablePayload);

            isNotifyModalSuccess.value = true;
            notifyEmail.value = '';
          } catch (error) {
            DataDogLogs.error(
              '[Allbirds section] - Product ATC Bar - handleNotifySubmit - Error submitting notify form',
              {
                origination,
              },
              error,
            );
            isNotifyModalError.value = true;
            notifyEmail.value = '';
          }
        };

        const setupSizePopoverTimeline = () => {
          if (
            !sizePopoverContainerRef.value ||
            !sizePopoverBackgroundRef.value ||
            !sizePopoverContentRef.value
          )
            return;

          const popoverContainer = sizePopoverContainerRef.value;
          const background = sizePopoverBackgroundRef.value;
          const content = sizePopoverContentRef.value;

          const timeline = gsap.timeline({
            onReverseComplete: () => {
              gsap.set(popoverContainer, { autoAlpha: 0, pointerEvents: 'none' });
            },
            onStart: () => {
              gsap.set(popoverContainer, { autoAlpha: 1, pointerEvents: 'auto' });
            },
            paused: true,
          });

          timeline
            .fromTo(
              background,
              { scaleY: 0, transformOrigin: 'bottom' },
              {
                duration: 0.3,
                ease: ANIMATION_EASES.POWER3_OUT,
                scaleY: 1,
              },
            )
            .fromTo(
              content,
              { opacity: 0 },
              {
                duration: 0.3,
                ease: ANIMATION_EASES.SINE_OUT,
                opacity: 1,
              },
              '<0.1',
            );

          sizePopoverTimeline.value = timeline;

          gsap.set(popoverContainer, { autoAlpha: 0 });
          gsap.set(content, { opacity: 0 });
          gsap.set(background, { scaleY: 0, transformOrigin: 'bottom' });
        };

        const updateURLWithSize = (variant) => {
          if (!variant) return;

          updateHistory({
            searchParams: {
              size: variant.titleHandleized,
              variant: String(variant.id),
            },
          });
        };

        const handleOpenGiftModalClick = () => {
          isSendAsGiftModalOpen.value = true;
        };

        const closeSendAsGiftModal = () => {
          isSendAsGiftModalOpen.value = false;
        };

        const handleSendAsGiftModalConfirm = async () => {
          if (!isValidDate(sendAsGiftModalSendOn.value)) {
            DataDogLogs.error(
              '[Allbirds section] - Product ATC Bar - handleSendAsGiftModalConfirm - Invalid date',
              {
                sendAsGiftModalSendOn: sendAsGiftModalSendOn.value,
              },
              new Error('Invalid date'),
            );

            return;
          }

          const giftCardLineItemProperties = buildGiftCardLineItemProperties({
            emailRecipient: sendAsGiftModalToEmail.value,
            messageRecipient: sendAsGiftModalGreeting.value,
            nameRecipient: sendAsGiftModalToName.value,
            sendOn: sendAsGiftModalSendOn.value,
          });

          try {
            // Add the gift card to the cart
            const updatedCart = await addSingleVariant(
              activeVariant.value.id,
              1,
              giftCardLineItemProperties,
            );

            // Send Analytics assuming this section is on the PDP
            RudderstackClient.productAdded({
              ajaxCartItems: updatedCart?.items,
              productAddedShopify: window?.ALLBIRDS?.page?.productDetailsPage?.product,
              productAddedSource: TRACK_CART_SOURCE.PDP,
              productSizeSelected: activeVariant?.value?.title,
            });
          } catch (error) {
            DataDogLogs.error(
              '[Allbirds section] - Product ATC Bar - handleSendAsGiftModalConfirm - Error adding gift card to cart',
              {
                giftCardLineItemProperties,
              },
              error,
            );
          }

          resetSendAsGiftModal();
          isSendAsGiftModalOpen.value = false;
        };

        const resetSendAsGiftModal = () => {
          sendAsGiftModalToEmail.value = null;
          sendAsGiftModalToName.value = null;
          sendAsGiftModalGreeting.value = null;
          sendAsGiftModalSendOn.value = null;
        };

        const isValidEmail = () => {
          return isEmail(sendAsGiftModalToEmail.value);
        };

        const isValidDate = () => {
          return dateValidator(sendAsGiftModalSendOn.value);
        };

        const isValidName = () => {
          return nameValidator(sendAsGiftModalToName.value);
        };

        const isValidMessage = () => {
          return messageValidator(sendAsGiftModalGreeting.value);
        };

        const isSendAsGiftModalValid = () => {
          return (
            isValidEmail() &&
            isValidName() &&
            isValidMessage() &&
            (sendAsGiftModalSendOn.value === null || isValidDate())
          );
        };

        const selectVariant = (variantId) => {
          const variantElement = container.querySelector(`[data-variant-id="${variantId}"]`);
          if (!variantElement) return;

          const variant = currentVariants.value.find((v) => v.id === variantId);
          if (!variant) return;

          activeVariant.value = variant;
          isSizeSelected.value = true;
          isUnavailableSelected.value = !variant.available;
          AnalyticsData.triggerProductViewed({ variantSize: variant.title });
        };

        const handleVariantClick = (variantId) => {
          selectVariant(variantId);
          closeSizePopover();
        };

        const handleTriggerKeydown = (event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            handleSizePopoverClick(event);
          }
        };

        const handleSizePopoverClick = (e) => {
          if (
            e.target === sizePopoverContainerRef.value ||
            sizePopoverContainerRef.value.contains(e.target)
          )
            return;

          e.stopPropagation();

          isSizePopoverOpen.value = !isSizePopoverOpen.value;
        };

        const handleKeydown = (e) => {
          if (e.key === 'Escape') {
            if (isSizePopoverOpen.value) {
              closeSizePopover();
            }
            if (isNotifyModalOpen.value) {
              closeNotifyModal();
            }
          }
        };

        const handleOutsideClick = (e) => {
          if (!isSizePopoverOpen.value) return;

          if (
            sizePopoverTriggerRef.value &&
            e.target !== sizePopoverTriggerRef.value &&
            sizePopoverContainerRef.value &&
            !sizePopoverContainerRef.value.contains(e.target)
          ) {
            closeSizePopover();
          }
        };

        const handleAddToCartClick = async () => {
          if (!activeVariant.value) return;
          cartVisibility.openDrawer();
          const isFinalSale = productTags.value.includes('FINAL SALE');
          const compareAtPrice = activeVariant.value.compareAtPrice;
          const price = activeVariant.value.price;

          const itemCartProperties = getItemCartProperties({
            colorName: activeVariant.value.colorName,
            compareAtPrice: compareAtPrice > price ? compareAtPrice : null,
            isFinalSale,
            masterName: activeVariant.value.productTitle,
          });

          try {
            // Add the item to the cart
            const updatedCart = await addSingleVariant(
              activeVariant.value.id,
              1,
              itemCartProperties,
            );

            // Send Analytics assuming this section is on the PDP
            RudderstackClient.productAdded({
              ajaxCartItems: updatedCart?.items,
              productAddedShopify: window?.ALLBIRDS?.page?.productDetailsPage?.product,
              productAddedSource: TRACK_CART_SOURCE.PDP,
              productSizeSelected: activeVariant?.value?.title,
            });
          } catch (error) {
            DataDogLogs.error(
              '[Allbirds section] - Product ATC Bar - handleAddToCartClick - Error adding item to cart',
              {
                itemCartProperties,
              },
              error,
            );
          }
        };

        const handleFloatingSelectSizeClick = () => {
          if (isMobile.value) {
            const selectSizeButton = container.querySelector('#select-size-button');
            selectSizeButton?.scrollIntoView({
              behavior: 'smooth',
              block: 'center',
            });
          } else {
            window.scrollTo({
              behavior: 'smooth',
              top: 0,
            });
            gsap.delayedCall(0.6, () => {
              openSizePopover();
            });
          }
        };

        const handleElementActivationInPopover = (event) => {
          const targetElement = event.target;
          const variantLi = targetElement.closest('li[data-variant-id]');

          if (variantLi) {
            event.preventDefault();
            const variantId = Number(variantLi.dataset.variantId);
            if (variantId) {
              handleVariantClick(variantId);
            }
          }
        };

        const handleDetailsButtonClick = () => {
          const productDetails = document.querySelector('[data-section-type="product-details"]');
          if (productDetails) {
            const productDetailsTop =
              productDetails.getBoundingClientRect().top + window.scrollY - 20;
            window.scrollTo({
              behavior: 'smooth',
              top: productDetailsTop,
            });
          }
        };

        useFocusManagement({
          containerRef: sizePopoverContainerRef,
          focusFirstOnActivate: true,
          focusTriggerElementRef: sizePopoverTriggerRef,
          isActive: isSizePopoverOpen,
          onElementActivated: handleElementActivationInPopover,
          shouldRestoreFocus: true,
          trapFocus: true,
        });

        watch(activeVariant, (newVal) => {
          if (newVal) {
            updateURLWithSize(newVal);
          }
        });

        watch(isSizePopoverOpen, (isOpen) => {
          if (!sizePopoverTimeline.value) return;

          if (isOpen) {
            sizePopoverTimeline.value.timeScale(1).play();
          } else {
            sizePopoverTimeline.value.timeScale(1.5).reverse();
          }
        });

        onMounted(() => {
          initializeElementRefs();
          setupVariants();
          try {
            productTags.value = JSON.parse(container.dataset.productTags || '[]');
          } catch (error) {
            DataDogLogs.error(
              '[Allbirds section] - Product ATC Bar - onMounted - Error parsing product tags',
              {},
              error,
            );
            productTags.value = [];
          }

          const url = new URL(window.location.href);
          const sizeParam = url.searchParams.get('size');
          const variantParam = url.searchParams.get('variant');

          if (sizeParam || variantParam) {
            const foundVariant = currentVariants.value.find(
              (variant) =>
                variant.titleHandleized.toLowerCase() === sizeParam?.toLowerCase() ||
                variant.id === Number(variantParam),
            );

            if (foundVariant) {
              activeVariant.value = foundVariant;
              isSizeSelected.value = true;
              isUnavailableSelected.value = !foundVariant.available;
            }
          }

          handleDocking(DOCKING_DIRECTIONS.UP);
          fadeIn();
          setupScrollTrigger();
          setupSizePopoverTimeline();

          window.addEventListener('keydown', handleKeydown);
          document.addEventListener('click', handleOutsideClick);
          if (sizePopoverTriggerRef.value) {
            sizePopoverTriggerRef.value.addEventListener('keydown', handleTriggerKeydown);
          }
        });

        onUnmounted(() => {
          if (scrollTriggerInstance.value) {
            scrollTriggerInstance.value.kill();
            scrollTriggerInstance.value = null;
          }

          if (sizePopoverTriggerRef.value) {
            sizePopoverTriggerRef.value.removeEventListener('keydown', handleTriggerKeydown);
          }
          window.removeEventListener('keydown', handleKeydown);
          document.removeEventListener('click', handleOutsideClick);
        });

        return {
          activeVariant,
          closeNotifyModal,
          closeSendAsGiftModal,
          handleAddToCartClick,
          handleDetailsButtonClick,
          handleFloatingSelectSizeClick,
          handleNotifySubmit,
          handleOpenGiftModalClick,
          handleSendAsGiftModalConfirm,
          handleSizePopoverClick,
          handleVariantClick,
          isNotifyModalError,
          isNotifyModalOpen,
          isNotifyModalSuccess,
          isSendAsGiftModalOpen,
          isSendAsGiftModalValid,
          isSizePopoverOpen,
          isSizeSelected,
          isUnavailableSelected,
          isValidDate,
          isValidEmail,
          isValidMessage,
          isValidName,
          notifyEmail,
          openNotifyModal,
          openSizeChart,
          sendAsGiftModalGreeting,
          sendAsGiftModalSendOn,
          sendAsGiftModalToEmail,
          sendAsGiftModalToName,
        };
      },
    }).mount(container);
  }
}

document.addEventListener('DOMContentLoaded', () => {
  new ProductATCBarSection();
});
